import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { User, UpdateUserData } from '@nimbitlabs/features';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, useToast } from '@nimbitlabs/ui';

const editUserSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(200, 'Name must be 200 characters or less'),
  email: z.string()
    .email('Please enter a valid email address')
    .max(250, 'Email must be 250 characters or less'),
  role: z.enum(['Admin', 'Editor', 'Viewer']),
  status: z.enum(['Active', 'Inactive']),
  avatar: z.string()
    .max(500, 'Avatar URL must be 500 characters or less')
    .optional(),
});

interface EditUserModalProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (userId: string, data: UpdateUserData) => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  open,
  onOpenChange,
  onSubmit,
}) => {
  const { toast } = useToast();
  
  const form = useForm<Omit<UpdateUserData, 'id'>>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'Viewer',
      status: 'Active',
      avatar: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name,
        email: user.email,
        role: user.role,
        status: user.status,
        avatar: user.avatar || '',
      });
    }
  }, [user, form]);

  const handleSubmit = async (data: UpdateUserData) => {
    if (!user) return;
    
    try {
      await onSubmit(user.id, data);
      toast({ title: 'Success', description: 'User updated successfully' });
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to update user:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update user',
        variant: 'destructive',
      });
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] shadow-large">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Edit User</DialogTitle>
          <DialogDescription>
            Update user information, role, and status.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex-1 flex flex-col overflow-hidden">
            <div className="space-y-4 flex-1 overflow-y-auto pr-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold">Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} className="transition-smooth" maxLength={200} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">Email Address</FormLabel>
                  <FormControl>
                    <Input placeholder="john.doe@company.com" type="email" {...field} className="transition-smooth" maxLength={250} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">Role</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="transition-smooth">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="shadow-medium">
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="Editor">Editor</SelectItem>
                      <SelectItem value="Viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="transition-smooth">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="shadow-medium">
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="avatar"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold">Avatar URL</FormLabel>
                  <FormControl>
                    <div className="flex gap-2 items-center">
                      {field.value && (
                        <img 
                          src={field.value} 
                          alt="Avatar preview" 
                          className="h-10 w-10 rounded-full object-cover border"
                        />
                      )}
                      <Input 
                        placeholder="Enter avatar URL" 
                        {...field}
                        className="flex-1 transition-smooth"
                        maxLength={500}
                      />
                    </div>
                  </FormControl>
                  <p className="text-xs text-muted-foreground">Paste a custom avatar URL</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="transition-smooth"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                size="sm"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};